import {
  DOCUMENTATION_TYPE,
  DocumentationStatus,
  GlobalVariableType,
  JOB_STATUS_TYPE,
  SYSTEM_TYPE,
  SaveVideoCondition,
  ScriptStatusEnum,
  VIRTUAL_USER_STATUS,
  VariableType,
} from "@ea/shared_types/next/ea.enums";
import { Paths } from "@ea/shared_types/next/ea.utils.types";
import { Palette } from "@mui/material/styles";
import { ElementType } from "react";
import {
  ChangeCircleIcon,
  CheckCircleIcon,
  CodeIcon,
  DeviceHubIcon,
  ForwardIcon,
  WarningIcon,
} from "../../icons";
import { default as enUITranslations } from "./../../translations/lang/en";

const STATUS_COLOR: Record<string, Paths<Palette>> = {
  SUCCESS: "success.main",
  SUCCESS_DARK: "success.dark",
  SUCCESS_400: "success.400",
  SUCCESS_600: "success.600",
  SUCCESS_800: "success.800",
  ERROR: "error.main",
  WARNING: "warning.main",
  WARNING_600: "warning.600",
  INFO: "info.main",
  SECONDARY_INFO: "secondaryInfo.main",
  NEUTRAL: "neutral.main",
  NEUTRAL_DARK: "neutral.dark",
  NEUTRAL_900: "neutral.900",
};

type Colors = typeof STATUS_COLOR;

export type StatusType = keyof (typeof enUITranslations)["Status"];

const createStatusStyle = (
  content: ElementType | StatusType,
  color: Colors[keyof Colors],
  variant: string | null = null,
) => ({
  content,
  color,
  variant,
});

const icon = {
  SUCCESS: createStatusStyle(CheckCircleIcon, STATUS_COLOR.SUCCESS),
  CHANGE_CIRCLE: createStatusStyle(ChangeCircleIcon, STATUS_COLOR.NEUTRAL),
  WARNING: createStatusStyle(WarningIcon, STATUS_COLOR.WARNING),
  WARNING_ERROR: createStatusStyle(WarningIcon, STATUS_COLOR.ERROR),
};

const state = {
  TERMINATED: createStatusStyle("terminated", STATUS_COLOR.NEUTRAL_DARK),
  FINISHED: createStatusStyle("finished", STATUS_COLOR.SUCCESS_400),
  RUNNING: createStatusStyle("running", STATUS_COLOR.SUCCESS_600),
  NONE: createStatusStyle("none", STATUS_COLOR.NEUTRAL),
  PAUSED: createStatusStyle("paused", STATUS_COLOR.INFO),
  ACTIVE: createStatusStyle("active", STATUS_COLOR.SUCCESS),
  INACTIVE: createStatusStyle("inactive", STATUS_COLOR.WARNING),
  active: createStatusStyle("active", STATUS_COLOR.SUCCESS),
  inactive: createStatusStyle("inactive", STATUS_COLOR.WARNING),
  removed: createStatusStyle("removed", STATUS_COLOR.NEUTRAL),
};

const status = {
  SUCCESS: createStatusStyle("success", STATUS_COLOR.SUCCESS),
  ERROR: createStatusStyle("error", STATUS_COLOR.ERROR),
  WARNING: createStatusStyle("warning", STATUS_COLOR.WARNING),
  NONE: createStatusStyle("none", STATUS_COLOR.NEUTRAL),
  UNTESTED: createStatusStyle("untested", STATUS_COLOR.NEUTRAL),
  COMPLETED: createStatusStyle("completed", STATUS_COLOR.SUCCESS),
  NOT_COMPLETED: createStatusStyle("notCompleted", STATUS_COLOR.WARNING),
  EXPIRED: createStatusStyle("expired", STATUS_COLOR.NEUTRAL),
  VALID: createStatusStyle("valid", STATUS_COLOR.SUCCESS),
};

const scripts = {
  [ScriptStatusEnum.DRAFT]: createStatusStyle("draft", STATUS_COLOR.INFO),
  [ScriptStatusEnum.IN_PROGRESS]: createStatusStyle("inProgress", STATUS_COLOR.SUCCESS_400),
  [ScriptStatusEnum.READY_FOR_REVIEW]: createStatusStyle("readyForReview", STATUS_COLOR.WARNING),
  [ScriptStatusEnum.CLOSED]: createStatusStyle("closed", STATUS_COLOR.NEUTRAL_900),
  [ScriptStatusEnum.PUBLISHED]: createStatusStyle("published", STATUS_COLOR.SUCCESS_400),
  [ScriptStatusEnum.READY_TO_TEST]: createStatusStyle("readyToTest", STATUS_COLOR.SUCCESS_600),
  LINKED: createStatusStyle("linked", STATUS_COLOR.SECONDARY_INFO),
  O: createStatusStyle("o", STATUS_COLOR.INFO),
  ICON_DEVICE_HUB_BADGE: createStatusStyle(DeviceHubIcon, STATUS_COLOR.INFO),
  ICON_CODE_BADGE: createStatusStyle(CodeIcon, STATUS_COLOR.INFO),
  ICON_FORWARD_BADGE: createStatusStyle(ForwardIcon, STATUS_COLOR.SUCCESS_600),
};

const doc = {
  [DocumentationStatus.DRAFT]: createStatusStyle("draft", STATUS_COLOR.NEUTRAL_DARK),
  [DocumentationStatus.PUBLISHED]: createStatusStyle("published", STATUS_COLOR.SUCCESS_DARK),
  [DocumentationStatus.PENDING]: createStatusStyle("pending", STATUS_COLOR.NEUTRAL),
};

const docType = {
  [DOCUMENTATION_TYPE.VIDEO]: createStatusStyle("video", STATUS_COLOR.SUCCESS_600, "outlined"),
  [DOCUMENTATION_TYPE.DOCUMENT]: createStatusStyle("document", STATUS_COLOR.INFO, "outlined"),
};

const envType = {
  [SYSTEM_TYPE.TEST]: createStatusStyle("test", STATUS_COLOR.WARNING_600, "outlined"),
  [SYSTEM_TYPE.DEV]: createStatusStyle("development", STATUS_COLOR.NEUTRAL_900, "outlined"),
  [SYSTEM_TYPE.PROD]: createStatusStyle("production", STATUS_COLOR.SUCCESS_600, "outlined"),
  [SYSTEM_TYPE.UAT]: createStatusStyle("uat", STATUS_COLOR.INFO, "outlined"),
  [SYSTEM_TYPE.OTHER]: createStatusStyle("other", STATUS_COLOR.NEUTRAL, "outlined"),
};

const vu = {
  [VIRTUAL_USER_STATUS.AUTHENTICATED]: createStatusStyle("authenticated", STATUS_COLOR.SUCCESS),
  [VIRTUAL_USER_STATUS.ERROR]: createStatusStyle("error", STATUS_COLOR.ERROR),
  [VIRTUAL_USER_STATUS.NOT_AUTHENTICATED]: createStatusStyle(
    "notAuthenticated",
    STATUS_COLOR.WARNING,
  ),
  [VIRTUAL_USER_STATUS.AUTHENTICATING]: createStatusStyle("authenticating", STATUS_COLOR.NEUTRAL),
};

const scheduler = {
  [JOB_STATUS_TYPE.ACTIVE]: createStatusStyle("active", STATUS_COLOR.SUCCESS),
  [JOB_STATUS_TYPE.INACTIVE]: createStatusStyle("inactive", STATUS_COLOR.WARNING),
  [JOB_STATUS_TYPE.COMPLETED]: createStatusStyle("completed", STATUS_COLOR.SUCCESS_800),
  [JOB_STATUS_TYPE.RUNNING]: createStatusStyle("running", STATUS_COLOR.SUCCESS_600),
  [JOB_STATUS_TYPE.CLOSED]: createStatusStyle("closed", STATUS_COLOR.NEUTRAL_900),
};

const variables = {
  [VariableType.DataSource]: createStatusStyle("datasource", STATUS_COLOR.NEUTRAL),
  [VariableType.Normal]: createStatusStyle("normal", STATUS_COLOR.NEUTRAL),
  [VariableType.Sequence]: createStatusStyle("sequence", STATUS_COLOR.NEUTRAL),
  [GlobalVariableType.CONSTANT]: createStatusStyle("constant", STATUS_COLOR.NEUTRAL, "outlined"),
  [GlobalVariableType.MUTABLE]: createStatusStyle("mutable", STATUS_COLOR.NEUTRAL, "outlined"),
};

const conditions = {
  [SaveVideoCondition.ON_SUCCESS]: createStatusStyle("success", STATUS_COLOR.SUCCESS),
  [SaveVideoCondition.ON_ERROR]: createStatusStyle("failure", STATUS_COLOR.ERROR),
  [SaveVideoCondition.ALWAYS]: createStatusStyle("always", STATUS_COLOR.NEUTRAL),
};

type IconStatusKeys = keyof typeof icon;
type StateStatusKeys = keyof typeof state;
type StatusStatusKeys = keyof typeof status;
type ScriptsStatusKeys = keyof typeof scripts;
type DocStatusKeys = keyof typeof doc;
type DocTypeStatusKeys = keyof typeof docType;
type EnvTypeStatusKeys = keyof typeof envType;
type VuStateKeys = keyof typeof vu;
type SchedulerStatusKeys = keyof typeof scheduler;
type VariablesStatusKeys = keyof typeof variables;
type ConditionsStatusKeys = keyof typeof conditions;

export type StatusKeys =
  | `${"icon"}.${IconStatusKeys}`
  | `${"state"}.${StateStatusKeys}`
  | `${"status"}.${StatusStatusKeys}`
  | `${"scripts"}.${ScriptsStatusKeys}`
  | `${"doc"}.${DocStatusKeys}`
  | `${"docType"}.${DocTypeStatusKeys}`
  | `${"envType"}.${EnvTypeStatusKeys}`
  | `${"vu"}.${VuStateKeys}`
  | `${"scheduler"}.${SchedulerStatusKeys}`
  | `${"var"}.${VariablesStatusKeys}`
  | `${"conditions"}.${ConditionsStatusKeys}`;

const prefixKeys = (obj: object, prefix: string) =>
  Object.fromEntries(Object.entries(obj).map(([key, value]) => [`${prefix}.${key}`, value]));

export const statusStyles = {
  ...prefixKeys(icon, "icon"),
  ...prefixKeys(state, "state"),
  ...prefixKeys(status, "status"),
  ...prefixKeys(scripts, "scripts"),
  ...prefixKeys(doc, "doc"),
  ...prefixKeys(docType, "docType"),
  ...prefixKeys(envType, "envType"),
  ...prefixKeys(vu, "vu"),
  ...prefixKeys(variables, "var"),
  ...prefixKeys(scheduler, "scheduler"),
  ...prefixKeys(conditions, "conditions"),
};
